/**
 * API Client — Centralized HTTP client for the backend API.
 * Handles auth tokens, refresh, and error handling.
 */

export const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api.openhubr.com";

// Token storage (in-memory + localStorage for persistence)
let accessToken: string | null = null;
let refreshToken: string | null = null;

const TOKEN_KEY = "openhub_access_token";
const REFRESH_KEY = "openhub_refresh_token";

export function getAccessToken(): string | null {
  if (!accessToken) {
    accessToken = localStorage.getItem(TOKEN_KEY);
  }
  return accessToken;
}

export function setTokens(access: string, refresh: string | null) {
  accessToken = access;
  if (refresh) refreshToken = refresh;
  localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

function getRefreshToken(): string | null {
  if (!refreshToken) {
    refreshToken = localStorage.getItem(REFRESH_KEY);
  }
  return refreshToken;
}

let refreshInProgress: Promise<boolean> | null = null;

export async function tryRefreshToken(): Promise<boolean> {
  // Prevent concurrent refresh calls
  if (refreshInProgress) return refreshInProgress;

  refreshInProgress = (async () => {
    const rt = getRefreshToken();
    if (!rt) return false;

    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt }),
      });

      if (!res.ok) {
        clearTokens();
        return false;
      }

      const data = await res.json();
      setTokens(data.accessToken, null);
      return true;
    } catch {
      clearTokens();
      return false;
    } finally {
      refreshInProgress = null;
    }
  })();

  return refreshInProgress;
}

function isTokenExpiringSoon(): boolean {
  const token = getAccessToken();
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = payload.exp;
    if (!exp) return true;
    // Refresh if less than 5 minutes remaining
    return (exp * 1000 - Date.now()) < 5 * 60 * 1000;
  } catch {
    return true;
  }
}

export async function ensureFreshToken(): Promise<void> {
  if (isTokenExpiringSoon() && getRefreshToken()) {
    await tryRefreshToken();
  }
}

/**
 * Make an authenticated API request.
 * Automatically refreshes token on 401.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  // Proactively refresh token if expiring within 5 minutes
  await ensureFreshToken();

  const token = getAccessToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  // Auto-refresh on 401
  if (res.status === 401 && token) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      headers["Authorization"] = `Bearer ${getAccessToken()}`;
      res = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers,
      });
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    // Attach the full parsed body on the error so callers can key on the
    // server's `error_code` / Postgres `code` fields. Without this, toast
    // mappings for signup flows (e.g. "email_domain_not_allowed" vs
    // "registration_closed" vs "full") can't distinguish cases since
    // `message` alone is too coarse.
    throw new ApiError(body.error || res.statusText, res.status, body);
  }

  return res.json();
}

export class ApiError extends Error {
  status: number;
  body: Record<string, unknown>;
  constructor(message: string, status: number, body: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}
